<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              @click="showCreateModal"
            >
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(title)="data">
            <span
              class="d-block text-truncate"
              style="max-width: 200px"
            >
              {{ getDisplayTitle(data.item) }}
            </span>
          </template>
          <template #cell(description)="data">
            <span
              class="d-block text-truncate"
              style="max-width: 250px"
            >
              {{ data.item.description }}
            </span>
          </template>
          <template #cell(date)="data">
            <template v-if="data.item.isRepeatEveryYear">
              {{ data.item.date | formatDate('MMMM D') }}
            </template>
            <template v-else>
              {{ data.item.date | formatDate('LL') }}
            </template>
          </template>
          <template #cell(type)="data">
            <b-badge
              v-if="data.item.isRepeatEveryYear"
              pill
              variant="warning"
              badge-glow
            >
              {{ $t('field.repeatAnnually') }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="info"
              badge-glow
            >
              {{ $t('field.applyOnce') }}
            </b-badge>
          </template>
          <template #cell(createdAt)="row">
            {{ row.item.createdAt | formatDate('LL HH:mm') }}
          </template>
          <template #front-button="row">
            <b-button
              v-if="$can('update', permissionType)"
              v-b-tooltip.hover
              :title="$t('general.update')"
              variant="primary"
              pill
              size="sm"
              @click="editItem(row.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <!-- Form Modal -->
    <form-modal
      ref="formModal"
      :is-edit="isEdit"
      :item="selectedItem"
      @submit="handleSubmit"
      @hide="handleHide"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormGroup,
  BInputGroup,
  BAvatar,
  BBadge,
  BLink,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import SearchInputs from './searchInput'
import tableFields from './tableField'
import FormModal from './FormModal.vue'

const HolidayRepository = Repository.get('holiday')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BAvatar,
    BLink,
    BFormGroup,
    BInputGroup,
    BBadge,
    BSpinner,
    NPagination,
    NSearchInput,
    NSearchContainer,
    NTable,
    vSelect,
    FormModal,
  },

  data() {
    return {
      items: [],
      loading: false,
      resendLoading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
      },
      avatarText,
      isEdit: false,
      selectedItem: null,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return !!this.$route.query.name || !!this.$route.query.isEnable
    },
  },
  watch: {
    perPage(value) {
      this.list(1)
    },
  },
  mounted() {
    this.getData(this.query.page)
  },
  methods: {
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      HolidayRepository.list({
        ...this.query,
        departmentId: this.$route.params.departmentId || null,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = [...data]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    showCreateModal() {
      this.isEdit = false
      this.selectedItem = null
      this.$refs.formModal.show()
    },
    editItem(item) {
      this.isEdit = true
      this.selectedItem = {
        id: item.id,
        title: item.title,
        date: item.date,
        description: item.description,
        isRepeatEveryYear: item.isRepeatEveryYear,
        departmentId: this.$route.params.departmentId,
      }
      this.$nextTick(() => {
        this.$refs.formModal.show()
      })
    },
    handleSubmit(formData) {
      const action = this.isEdit
        ? HolidayRepository.update(this.selectedItem.id, formData)
        : HolidayRepository.create(formData)

      action
        .then(() => {
          this.getData()
          this.$refs.formModal.hide()
        })
        .catch(error => {
          console.error(error)
        })
    },
    handleHide() {
      this.selectedItem = null
      this.isEdit = false
    },
    confirmDestroy(id, title) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: title,
          }),
          {
            title: this.$t('alert.areYouSure'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
    destroy(id) {
      HolidayRepository.destroy(id)
        .then(() => {
          this.getData()
        })
        .catch(() => {})
    },
    getDisplayTitle(item) {
      // Handle multi-language title display
      if (item.title && typeof item.title === 'object') {
        const currentLocale = this.$i18n.locale.split('-')[0]
        return item.title[currentLocale] || item.title.en || item.title.zh || item.title.km || item.title.vi || '-'
      }
      return item.title || '-'
    },
  },
  setup() {
    const permissionType = 'manage-holiday'
    const route = 'holiday'
    const fields = tableFields
    const searchFields = SearchInputs
    return {
      permissionType, route, fields, searchFields,
    }
  },
}
</script>

<style lang="scss" scoped>
  .btn-icon {
    padding: 0.5rem;
    margin: 0 0.2rem;
  }
</style>
